// client/src/components/UnifiedAdvancedSearch.tsx
//
// Layout-optimised variant of AdvancedSearch designed for use inside
// UnifiedSearchBar.  Key differences from the original AdvancedSearch:
//
//   1. No outer container border / background — the caller (UnifiedSearchBar's
//      panelSide) provides the single visible box.
//   2. "+ Add Rule", "Clear", and "Search" are rendered on one row:
//      [+ Add Rule]  ───────────────  [Clear] [Search]
//   3. "Save as Preset" controls live in the UnifiedSearchBar panel header,
//      not inside this component.
//
// AdvancedSearch.tsx is left untouched.
import React, { useEffect, useState, type JSX } from 'react';
import { Plus, Minus } from 'lucide-react';
import {
  FieldConfig,
  FilterGroup,
  SearchRule,
  LogicalOperator,
  FieldOperator,
  AdvancedSearchQuery,
  getOperatorsForDataType,
  OPERATOR_REGISTRY,
  createEmptyFilterGroup,
  createEmptySearchRule,
} from './TableSearch/types';
import AsyncSearchSelect from './TableSearch/AsyncSearchSelect';
import {
  GRAY_100,
  GRAY_200,
  GRAY_300,
  GRAY_400,
  GRAY_500,
  GRAY_700,
} from 'theme/grayScale';
import { COLOR_DANGER, COLOR_WARNING_BG } from 'theme/colors';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface UnifiedAdvancedSearchProps {
  fieldConfigs: FieldConfig[];
  query: AdvancedSearchQuery;
  onSearch: (query: AdvancedSearchQuery) => void;
  onClear: () => void;
  /** Fires on every rule change (not just Search submit) — used by the preset save footer. */
  onQueryChange?: (query: AdvancedSearchQuery) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const UnifiedAdvancedSearch: React.FC<UnifiedAdvancedSearchProps> = ({
  fieldConfigs,
  query,
  onSearch,
  onClear,
  onQueryChange,
}) => {
  const normalizeQuery = (q: AdvancedSearchQuery): AdvancedSearchQuery => {
    const groups =
      q.filterGroups.length > 0 ? q.filterGroups : [createEmptyFilterGroup()];
    return {
      ...q,
      filterGroups: groups.map(group => ({
        ...group,
        rules: group.rules.map(rule => ({
          ...rule,
          value: rule.value ?? '',
        })),
      })),
    };
  };

  const [localQuery, setLocalQuery] = useState(normalizeQuery(query));

  useEffect(() => {
    onQueryChange?.(localQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onQueryChange intentionally omitted: this effect should fire only when localQuery changes, not on every parent re-render that creates a new onQueryChange reference (not all callers memoize it, e.g. ProductsAdvancedSearch's handleQueryChange)
  }, [localQuery]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleUpdateGroupOperator = (
    groupId: string,
    operator: LogicalOperator
  ) => {
    setLocalQuery(prev => ({
      ...prev,
      filterGroups: prev.filterGroups.map(g =>
        g.id === groupId ? { ...g, logicalOperator: operator } : g
      ),
    }));
  };

  const handleAddRule = (groupId: string) => {
    setLocalQuery(prev => ({
      ...prev,
      filterGroups: prev.filterGroups.map(g =>
        g.id === groupId
          ? { ...g, rules: [...g.rules, createEmptySearchRule()] }
          : g
      ),
    }));
  };

  const handleRemoveRule = (groupId: string, ruleId: string) => {
    setLocalQuery(prev => ({
      ...prev,
      filterGroups: prev.filterGroups.map(g =>
        g.id === groupId
          ? { ...g, rules: g.rules.filter(r => r.id !== ruleId) }
          : g
      ),
    }));
  };

  const handleUpdateRuleField = (
    groupId: string,
    ruleId: string,
    field: string
  ) => {
    const fieldConfig = fieldConfigs.find(f => f.key === field);
    const defaultOperator = fieldConfig
      ? getOperatorsForDataType(fieldConfig.dataType, fieldConfig.nullable)[0]
      : 'equals';

    setLocalQuery(prev => ({
      ...prev,
      filterGroups: prev.filterGroups.map(g =>
        g.id === groupId
          ? {
              ...g,
              rules: g.rules.map(r =>
                r.id === ruleId
                  ? { ...r, field, operator: defaultOperator, value: '' }
                  : r
              ),
            }
          : g
      ),
    }));
  };

  const handleUpdateRuleOperator = (
    groupId: string,
    ruleId: string,
    operator: FieldOperator
  ) => {
    setLocalQuery(prev => ({
      ...prev,
      filterGroups: prev.filterGroups.map(g =>
        g.id === groupId
          ? {
              ...g,
              rules: g.rules.map(r =>
                r.id === ruleId ? { ...r, operator } : r
              ),
            }
          : g
      ),
    }));
  };

  const handleUpdateRuleValue = (
    groupId: string,
    ruleId: string,
    value: string | number | Date | [Date, Date],
    label?: string | null
  ) => {
    setLocalQuery(prev => ({
      ...prev,
      filterGroups: prev.filterGroups.map(g =>
        g.id === groupId
          ? {
              ...g,
              rules: g.rules.map(r =>
                r.id === ruleId ? { ...r, value, label: label ?? undefined } : r
              ),
            }
          : g
      ),
    }));
  };

  const handleSearch = () => onSearch(localQuery);

  const handleClear = () => {
    const emptyQuery: AdvancedSearchQuery = {
      filterGroups: [createEmptyFilterGroup()],
      groupOperator: 'AND' as LogicalOperator,
    };
    setLocalQuery(emptyQuery);
    onClear();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // ── Value input renderer ──────────────────────────────────────────────────

  const getFieldConfig = (key: string): FieldConfig | undefined =>
    fieldConfigs.find(f => f.key === key);

  const renderValueInput = (
    group: FilterGroup,
    rule: SearchRule
  ): JSX.Element => {
    const fieldConfig = getFieldConfig(rule.field);

    if (!fieldConfig) {
      return (
        <input
          type="text"
          placeholder="Select Value"
          value=""
          disabled
          style={styles.inputDisabledStyle}
        />
      );
    }

    if (rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty') {
      return <span style={styles.noValueLabelStyle}>—</span>;
    }

    if (rule.operator === 'isBetween' && fieldConfig.dataType === 'date') {
      const dateRange = Array.isArray(rule.value) ? rule.value : ['', ''];
      return (
        <div style={styles.dateRangeContainerStyle}>
          <input
            type="date"
            value={(dateRange[0] as string) ?? ''}
            onChange={e => {
              const newRange = [e.target.value, dateRange[1]];
              handleUpdateRuleValue(
                group.id,
                rule.id,
                newRange as [Date, Date]
              );
            }}
            style={styles.dateRangeInputStyle}
            placeholder="Start date"
          />
          <span style={styles.dateRangeSeparatorStyle}>to</span>
          <input
            type="date"
            value={(dateRange[1] as string) ?? ''}
            onChange={e => {
              const newRange = [dateRange[0], e.target.value];
              handleUpdateRuleValue(
                group.id,
                rule.id,
                newRange as [Date, Date]
              );
            }}
            style={styles.dateRangeInputStyle}
            placeholder="End date"
          />
        </div>
      );
    }

    switch (fieldConfig.dataType) {
      case 'string':
        return (
          <input
            type="text"
            placeholder="Enter value"
            value={(rule.value as string) ?? ''}
            onChange={e =>
              handleUpdateRuleValue(group.id, rule.id, e.target.value)
            }
            style={styles.inputStyle}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            placeholder="Enter number"
            value={typeof rule.value === 'number' ? rule.value : ''}
            onChange={e =>
              handleUpdateRuleValue(
                group.id,
                rule.id,
                e.target.value ? parseFloat(e.target.value) : ''
              )
            }
            style={styles.inputStyle}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={(rule.value as string) ?? ''}
            onChange={e =>
              handleUpdateRuleValue(group.id, rule.id, e.target.value)
            }
            style={styles.inputStyle}
          />
        );
      case 'selection':
        if (
          fieldConfig.apiEndpoint &&
          fieldConfig.searchField &&
          fieldConfig.valueField &&
          fieldConfig.labelField
        ) {
          return (
            <AsyncSearchSelect
              key={`${rule.field}-${fieldConfig.apiEndpoint}`}
              value={rule.value as string}
              initialLabel={rule.label}
              onChange={(newValue: string | null, newLabel?: string | null) =>
                handleUpdateRuleValue(
                  group.id,
                  rule.id,
                  newValue || '',
                  newLabel
                )
              }
              apiEndpoint={fieldConfig.apiEndpoint}
              searchField={fieldConfig.searchField}
              valueField={fieldConfig.valueField}
              labelField={fieldConfig.labelField}
              extraParams={fieldConfig.extraParams}
              placeholder="Select value"
            />
          );
        }
        return (
          <select
            value={(rule.value as string) ?? ''}
            onChange={e =>
              handleUpdateRuleValue(group.id, rule.id, e.target.value)
            }
            style={styles.selectStyle}>
            <option value="">Select value</option>
            {fieldConfig.options?.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      default:
        return (
          <input
            type="text"
            value=""
            disabled
            style={styles.inputDisabledStyle}
          />
        );
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const group = localQuery.filterGroups[0];

  return (
    <div onKeyDown={handleKeyDown}>
      {/* Rules */}
      <div style={styles.rulesContainerStyle}>
        {group.rules.length === 0 ? (
          <div style={styles.emptyRulesStyle}>
            No rules yet. Click &ldquo;Add Rule&rdquo; to get started.
          </div>
        ) : (
          group.rules.map((rule, ruleIndex) => (
            <div key={rule.id}>
              {/* AND / OR separator between rules */}
              {ruleIndex > 0 && (
                <div style={styles.ruleSeparatorStyle}>
                  <select
                    value={group.logicalOperator}
                    onChange={e =>
                      handleUpdateGroupOperator(
                        group.id,
                        e.target.value as LogicalOperator
                      )
                    }
                    style={styles.andOrSelectStyle}>
                    <option value="AND">And</option>
                    <option value="OR">Or</option>
                  </select>
                </div>
              )}

              {/* Rule row */}
              <div style={styles.ruleRowStyle}>
                <div style={styles.ruleFieldsStyle}>
                  {/* Field */}
                  <div style={styles.fieldColumnStyle}>
                    <select
                      value={rule.field ?? ''}
                      onChange={e =>
                        handleUpdateRuleField(group.id, rule.id, e.target.value)
                      }
                      style={styles.selectStyle}>
                      <option value="">Select Field</option>
                      {fieldConfigs.map(field => (
                        <option key={field.key} value={field.key}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Operator */}
                  <div style={styles.operatorColumnStyle}>
                    <select
                      value={rule.operator ?? ''}
                      onChange={e =>
                        handleUpdateRuleOperator(
                          group.id,
                          rule.id,
                          e.target.value as FieldOperator
                        )
                      }
                      disabled={!rule.field}
                      style={
                        rule.field
                          ? styles.selectStyle
                          : styles.selectDisabledStyle
                      }>
                      <option value="">Select Operator</option>
                      {rule.field &&
                        (() => {
                          const fc = getFieldConfig(rule.field);
                          return fc
                            ? getOperatorsForDataType(
                                fc.dataType,
                                fc.nullable
                              ).map(op => (
                                <option key={op} value={op}>
                                  {OPERATOR_REGISTRY[op].label}
                                </option>
                              ))
                            : null;
                        })()}
                    </select>
                  </div>

                  {/* Value */}
                  <div style={styles.valueColumnStyle}>
                    {renderValueInput(group, rule)}
                  </div>
                </div>

                {/* Remove rule */}
                <button
                  onClick={() => handleRemoveRule(group.id, rule.id)}
                  style={styles.removeRuleButtonStyle}
                  title="Remove Rule">
                  <Minus size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Bottom row: [+ Add Rule] ─────────── [Clear] [Search] */}
      <div style={styles.bottomRowStyle}>
        <button
          onClick={() => handleAddRule(group.id)}
          style={styles.addRuleButtonStyle}>
          <Plus size={16} /> Add Rule
        </button>
        <div style={styles.actionButtonsStyle}>
          <button onClick={handleClear} style={styles.clearButtonStyle}>
            Clear
          </button>
          <button onClick={handleSearch} style={styles.searchButtonStyle}>
            Search
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  rulesContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  emptyRulesStyle: {
    padding: '0.75rem',
    textAlign: 'center',
    color: GRAY_500,
    fontSize: '0.8125rem',
    fontStyle: 'italic',
  },
  ruleSeparatorStyle: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginLeft: '0.75rem',
    marginBottom: '0.375rem',
  },
  andOrSelectStyle: {
    padding: '0.2rem 0.4rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    fontSize: '0.6875rem',
    fontWeight: '500',
    backgroundColor: COLOR_WARNING_BG,
    cursor: 'pointer',
    minWidth: '55px',
  },
  ruleRowStyle: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    padding: '0.5rem',
    borderRadius: '0.25rem',
    border: `1px solid ${GRAY_200}`,
  },
  ruleFieldsStyle: {
    display: 'flex',
    gap: '0.5rem',
    flex: 1,
  },
  fieldColumnStyle: {
    flex: '1 1 180px',
  },
  operatorColumnStyle: {
    flex: '0 0 140px',
  },
  valueColumnStyle: {
    flex: '1 1 240px',
  },
  selectStyle: {
    padding: '0.4rem 0.5rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    width: '100%',
  },
  selectDisabledStyle: {
    padding: '0.4rem 0.5rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    backgroundColor: GRAY_100,
    color: GRAY_400,
    cursor: 'not-allowed',
    width: '100%',
  },
  inputStyle: {
    padding: '0.4rem 0.5rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    width: '100%',
  },
  inputDisabledStyle: {
    padding: '0.4rem 0.5rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    backgroundColor: GRAY_100,
    color: GRAY_400,
    cursor: 'not-allowed',
    width: '100%',
  },
  noValueLabelStyle: {
    display: 'flex',
    alignItems: 'center',
    color: GRAY_400,
    fontSize: '0.8125rem',
    fontStyle: 'italic',
    paddingLeft: '0.5rem',
  },
  removeRuleButtonStyle: {
    backgroundColor: 'transparent',
    color: COLOR_DANGER,
    border: `1px solid ${COLOR_DANGER}`,
    borderRadius: '0.25rem',
    padding: '0.4rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  bottomRowStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '0.5rem',
  },
  addRuleButtonStyle: {
    backgroundColor: 'transparent',
    color: '#2563eb',
    border: '1px solid #2563eb',
    borderRadius: '0.25rem',
    padding: '0.375rem 0.75rem',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  actionButtonsStyle: {
    display: 'flex',
    gap: '0.5rem',
  },
  clearButtonStyle: {
    padding: '0.4rem 0.875rem',
    backgroundColor: GRAY_200,
    color: GRAY_700,
    border: 'none',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: '500',
  },
  searchButtonStyle: {
    padding: '0.4rem 1.25rem',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    border: 'none',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    fontWeight: '500',
  },
  dateRangeContainerStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    width: '100%',
  },
  dateRangeInputStyle: {
    padding: '0.4rem 0.5rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.25rem',
    fontSize: '0.8125rem',
    flex: 1,
  },
  dateRangeSeparatorStyle: {
    fontSize: '0.6875rem',
    color: GRAY_500,
    fontWeight: '500',
    flexShrink: 0,
  },
};

export default UnifiedAdvancedSearch;
